import { useId, useState } from 'react';
import { Camera, ChevronDown, CircleAlert, CircleCheck, ImageOff, NotebookText, OctagonAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useSignedUrlState } from '@/hooks/useSignedUrl';
import { cn } from '@/lib/utils';
import {
    GRADE_LABEL,
    RESULT_LABEL,
    SLOT_LABEL,
    SLOT_ORDER,
    formatDayYear,
    formatKm,
    formatRoundMonth,
    itemLabel,
    lineGradeLabel,
    type InspectionChecklist,
    type InspectionGrade,
    type InspectionResult,
    type InspectionSlot,
    type InspectionVisit,
    type InspectionVisitLine,
    type InspectionVisitPhoto,
} from '@/lib/inspections';

/**
 * One visit as a certificate: the result banner, the date, the odometer, the inspector's first
 * name, the graded lines and the photos. Everything printed is a value the server returned — the
 * result included — so the card never works out a verdict of its own. A legacy row (no kind, no
 * lines) reads "Notes only". The details open on a tap, so a long history mounts no photos until
 * a driver asks for them.
 */

const BANNER: Record<InspectionResult, { className: string; Icon: typeof CircleCheck }> = {
    passed: {
        className: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
        Icon: CircleCheck,
    },
    passed_fix_soon: {
        className: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
        Icon: CircleAlert,
    },
    held: {
        className: 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-300',
        Icon: OctagonAlert,
    },
    notes_only: {
        className: 'bg-muted text-muted-foreground',
        Icon: NotebookText,
    },
};

const GRADE_TONE: Record<InspectionGrade, string> = {
    fix_now: 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300',
    fix_soon: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-300',
    fixed: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-300',
    not_needed: 'border-border bg-muted text-muted-foreground',
    ok: 'border-border bg-background text-foreground/80',
};

/** The checklist line the km since the last service is shown by. */
const SERVICING_ITEM = 'servicing';

/** The groups a certificate lists its lines in; OK lines follow as a compact list. */
const LINE_GROUPS: Array<{ grade: Exclude<InspectionGrade, 'ok'>; title: string }> = [
    { grade: 'fix_now', title: 'Must fix' },
    { grade: 'fix_soon', title: 'Fix soon' },
    { grade: 'fixed', title: 'Fixed' },
    { grade: 'not_needed', title: 'No fix needed' },
];

/** A grade, as a small chip. */
function GradeChip({ grade, label }: { grade: InspectionGrade; label: string }) {
    return (
        <span className={cn('inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold', GRADE_TONE[grade])}>
            {label}
        </span>
    );
}

/**
 * A photo thumbnail that opens full size on a tap. The file is signed once, on demand, and the
 * full-size view reuses the signed URL. A photo that cannot be signed or loaded says so and
 * offers a retry, rather than sitting as a placeholder that looks like one still loading: on a
 * Held card the photo is the evidence for the Must fix (decision 3).
 */
export function InspectionPhoto({ url, label, className }: { url: string; label: string; className?: string }) {
    const { url: signedUrl, status, retry } = useSignedUrlState(url);
    // The signed URL the browser failed to load (a missing object, a dropped connection).
    const [brokenUrl, setBrokenUrl] = useState<string | null>(null);
    const failed = status === 'error' || (signedUrl !== undefined && signedUrl === brokenUrl);
    const tile = cn('overflow-hidden rounded-lg border bg-muted', className);

    if (failed) {
        return (
            <button
                type="button"
                onClick={() => { setBrokenUrl(null); retry(); }}
                aria-label={`Photo could not be loaded: ${label}. Try again`}
                className={cn(
                    tile,
                    'flex flex-col items-center justify-center gap-1 text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:opacity-80',
                )}
            >
                <ImageOff className="h-5 w-5" aria-hidden="true" />
                <span className="text-[11px] font-medium">Retry</span>
            </button>
        );
    }

    if (status === 'loading' || !signedUrl) {
        return (
            <span role="img" aria-label={`Loading photo: ${label}`} className={cn(tile, 'flex items-center justify-center text-muted-foreground')}>
                <Camera className="h-5 w-5 animate-pulse" aria-hidden="true" />
            </span>
        );
    }

    return (
        <Dialog>
            <DialogTrigger asChild>
                <button
                    type="button"
                    aria-label={`Open photo: ${label}`}
                    className={cn(tile, 'block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:opacity-80')}
                >
                    <img src={signedUrl} alt={label} className="h-full w-full object-cover" onError={() => setBrokenUrl(signedUrl)} />
                </button>
            </DialogTrigger>
            <DialogContent aria-describedby={undefined} className="w-[calc(100vw-2rem)] max-w-3xl gap-3 p-3 sm:p-4">
                <DialogTitle className="pr-8 text-sm font-medium">{label}</DialogTitle>
                <img
                    src={signedUrl}
                    alt={label}
                    className="max-h-[70vh] w-full rounded-md bg-muted object-contain"
                    onError={() => setBrokenUrl(signedUrl)}
                />
                {/* The corner X is a 16px target; this one fills the width under the photo. */}
                <DialogClose asChild>
                    <Button type="button" variant="outline" className="h-11 w-full">Close</Button>
                </DialogClose>
            </DialogContent>
        </Dialog>
    );
}

function visitTitle(visit: InspectionVisit): string {
    if (visit.kind === 'recheck') return 'Re-check';
    if (visit.kind === 'monthly') return visit.period ? `${formatRoundMonth(visit.period)} inspection` : 'Monthly inspection';
    return 'Inspection';
}

function LineRow({
    line,
    checklist,
    photos,
    detail,
}: {
    line: InspectionVisitLine;
    checklist: InspectionChecklist;
    photos: InspectionVisitPhoto[];
    /** A reading that belongs beside this line: the km since the last service, on servicing. */
    detail?: string | null;
}) {
    const label = itemLabel(checklist, line.item);
    // The group heading already names the grade; a chip only adds what a re-check says on top
    // of it ("Still must fix", "Down to fix soon").
    const regrade = lineGradeLabel(line.grade, line.previous_grade);
    return (
        <li className="space-y-2 rounded-lg border border-border/60 p-3">
            <div className="flex items-start justify-between gap-3">
                <span className="text-sm font-medium">{label}</span>
                {regrade !== GRADE_LABEL[line.grade] && <GradeChip grade={line.grade} label={regrade} />}
            </div>
            {detail && <p className="text-xs text-muted-foreground tabular-nums">{detail}</p>}
            {line.note && <p className="text-sm text-muted-foreground">{line.note}</p>}
            {photos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {photos.map((p) => (
                        <InspectionPhoto key={p.id} url={p.url} label={label} className="h-16 w-16" />
                    ))}
                </div>
            )}
        </li>
    );
}

interface Props {
    visit: InspectionVisit;
    checklist: InspectionChecklist;
    defaultOpen?: boolean;
}

export default function InspectionCertificate({ visit, checklist, defaultOpen = false }: Props) {
    const [open, setOpen] = useState(defaultOpen);
    const detailsId = useId();
    const banner = BANNER[visit.result];
    const legacy = visit.kind === null;

    const meta = [
        formatDayYear(visit.inspected_on),
        visit.odometer_km !== null ? formatKm(visit.odometer_km) : null,
        visit.inspector_first_name ? `Inspected by ${visit.inspector_first_name}` : null,
    ].filter(Boolean).join(' · ');

    const photosByLine = new Map<number, InspectionVisitPhoto[]>();
    const slotPhotos = new Map<InspectionSlot, InspectionVisitPhoto>();
    const otherPhotos: InspectionVisitPhoto[] = [];
    for (const p of visit.photos) {
        if (p.line_id !== null) {
            photosByLine.set(p.line_id, [...(photosByLine.get(p.line_id) ?? []), p]);
        } else if (p.slot !== null && !slotPhotos.has(p.slot)) {
            slotPhotos.set(p.slot, p);
        } else {
            otherPhotos.push(p);
        }
    }
    const slotShots = SLOT_ORDER.flatMap((slot) => {
        const photo = slotPhotos.get(slot);
        return photo ? [{ slot, photo }] : [];
    });
    const okLines = visit.lines.filter((l) => l.grade === 'ok');
    const sinceService = visit.odometer_km !== null && visit.last_service_km !== null && visit.odometer_km >= visit.last_service_km
        ? visit.odometer_km - visit.last_service_km
        : null;
    // Shown by the servicing line (PLAN.md §9), so the reading sits beside the grade it informs;
    // only a visit with no servicing line lists it with the odometer change instead.
    const serviceNote = sinceService !== null ? `${formatKm(sinceService)} since last service` : null;
    const hasServicingLine = visit.lines.some((l) => l.item === SERVICING_ITEM);
    const detailFor = (line: InspectionVisitLine) => (line.item === SERVICING_ITEM ? serviceNote : null);

    return (
        <article className="overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm">
            <button
                type="button"
                aria-expanded={open}
                aria-controls={open ? detailsId : undefined}
                onClick={() => setOpen((o) => !o)}
                className="block w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            >
                <div className={cn('flex items-center justify-between gap-3 px-4 py-2 text-sm font-semibold', banner.className)}>
                    <span className="flex items-center gap-2">
                        <banner.Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                        {legacy ? 'Notes only' : RESULT_LABEL[visit.result]}
                    </span>
                    <span className="flex flex-wrap justify-end gap-1.5 text-[11px] font-semibold">
                        {visit.released && (
                            <span className="rounded-full bg-background/70 px-2 py-0.5 text-emerald-700 dark:text-emerald-300">Released</span>
                        )}
                        {visit.late && (
                            <span className="rounded-full bg-background/70 px-2 py-0.5 text-amber-700 dark:text-amber-300">Late</span>
                        )}
                    </span>
                </div>
                <div className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                        <p className="text-sm font-medium">{visitTitle(visit)}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{meta}</p>
                    </div>
                    <ChevronDown
                        className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200', open && 'rotate-180')}
                        aria-hidden="true"
                    />
                </div>
            </button>

            {open && (
                <div id={detailsId} className="space-y-5 border-t border-border/60 px-4 py-4">
                    {legacy && (
                        <p className="text-sm text-muted-foreground">Notes only — recorded before the checklist.</p>
                    )}

                    {(visit.odometer_change_km !== null || (serviceNote !== null && !hasServicingLine)) && (
                        <dl className="grid grid-cols-2 gap-3 text-sm">
                            {visit.odometer_change_km !== null && (
                                <div>
                                    <dt className="text-xs text-muted-foreground">Since the last reading</dt>
                                    <dd className="font-medium tabular-nums">
                                        {visit.odometer_change_km >= 0 ? '+' : '−'}{formatKm(Math.abs(visit.odometer_change_km))}
                                    </dd>
                                </div>
                            )}
                            {sinceService !== null && !hasServicingLine && (
                                <div>
                                    <dt className="text-xs text-muted-foreground">Since the last service</dt>
                                    <dd className="font-medium tabular-nums">{formatKm(sinceService)}</dd>
                                </div>
                            )}
                        </dl>
                    )}

                    {LINE_GROUPS.map(({ grade, title }) => {
                        const lines = visit.lines.filter((l) => l.grade === grade);
                        if (lines.length === 0) return null;
                        return (
                            <section key={grade} className="space-y-2">
                                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
                                <ul className="space-y-2">
                                    {lines.map((line) => (
                                        <LineRow
                                            key={line.id}
                                            line={line}
                                            checklist={checklist}
                                            photos={photosByLine.get(line.id) ?? []}
                                            detail={detailFor(line)}
                                        />
                                    ))}
                                </ul>
                            </section>
                        );
                    })}

                    {okLines.length > 0 && (
                        <section className="space-y-2">
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">OK</h3>
                            <ul className="flex flex-wrap gap-1.5">
                                {okLines.map((line) => {
                                    const detail = detailFor(line);
                                    return (
                                        <li key={line.id} className="rounded-full border border-border/60 px-2.5 py-1 text-xs">
                                            {itemLabel(checklist, line.item)}
                                            {detail && <span className="text-muted-foreground tabular-nums"> · {detail}</span>}
                                        </li>
                                    );
                                })}
                            </ul>
                        </section>
                    )}

                    {visit.remarks && (
                        <section className="space-y-1">
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                {legacy ? 'Notes' : 'Remarks'}
                            </h3>
                            <p className="whitespace-pre-line text-sm">{visit.remarks}</p>
                        </section>
                    )}

                    {slotShots.length > 0 && (
                        <section className="space-y-2">
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Photos of the car</h3>
                            <ul className="grid grid-cols-3 gap-2">
                                {slotShots.map(({ slot, photo }) => (
                                    <li key={slot} className="space-y-1">
                                        <InspectionPhoto url={photo.url} label={SLOT_LABEL[slot]} className="aspect-[4/3] w-full" />
                                        <p className="truncate text-[11px] text-muted-foreground">{SLOT_LABEL[slot]}</p>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {otherPhotos.length > 0 && (
                        <section className="space-y-2">
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Photos</h3>
                            <div className="flex flex-wrap gap-2">
                                {otherPhotos.map((p) => (
                                    <InspectionPhoto key={p.id} url={p.url} label="Inspection photo" className="h-20 w-20" />
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            )}
        </article>
    );
}
